import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { homeThingsToDoV2ContentItemContent$key } from "scmp-app/queries/__generated__/homeThingsToDoV2ContentItemContent.graphql";

import {
  ActionBar,
  Container,
  CoverImage,
  Headline,
  Section,
  StyledCoverEntityLink,
  StyledHeadlineEntityLink,
  TextColumn,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: homeThingsToDoV2ContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment homeThingsToDoV2ContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withSections: true
            withHeadline: true
            withActionBar: true
            withCoverImage: true
            withCoverImageSize500x500: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content);

  return (
    <Container className={className} ref={ref}>
      <TextColumn>
        <Section>{providers.sections()}</Section>
        <StyledHeadlineEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          reference={content}
        >
          <Headline>
            {providers.headline({
              preferSocialHeadline: true,
            })}
          </Headline>
        </StyledHeadlineEntityLink>
        <ActionBar>
          {providers.actionBar({
            displayDateVariant: "readingTime",
            isBookmarkVisible: false,
            isCommentsVisible: false,
            isIconVisible: false,
          })}
        </ActionBar>
      </TextColumn>
      <StyledCoverEntityLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size500x500",
              mobileUp: "size500x500",
              tabletUp: "size500x500",
            },
          })}
        </CoverImage>
      </StyledCoverEntityLink>
    </Container>
  );
};

Component.displayName = "ContentItemHomeThingsToDoV2";

export const ContentItemHomeThingsToDoV2 = withHighlightedHeadline(Component);
